import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Title,
    Paper,
    Text,
    Badge,
    Group,
    Stack,
    Loader,
    Alert,
    Grid,
    Button,
    Box,
    createStyles,
    Modal,
    NumberInput,
    Textarea,
    Select,
    ActionIcon,
    Menu,
    Divider,
    SimpleGrid,
    ScrollArea,
    ThemeIcon,
    Progress,
    rem,
} from '@mantine/core';
import {
    IconAlertCircle,
    IconArrowLeft,
    IconChartBar,
    IconFileText,
    IconShieldCheck,
    IconPercentage,
    IconDotsVertical,
    IconDoorExit,
    IconArrowDown,
    IconChevronDown,
    IconCalendar,
    IconUsers,
    IconChartPie,
    IconInfoCircle,
    IconPencil
} from '@tabler/icons-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db, functions } from '../../firebase/FirebaseCredential';
import { useAppSelector } from '../../hooks/hooks';
import { RootState } from '../../store/store';
import { toast } from 'react-toastify';
import PayrollEmployeeSidePanel from './PayrollEmployeeSidePanel';
import { ReusableModal } from "../UIComponent/ReusableModal";
import { ExitDetailsForm } from "../userComponent/ExitDetailsForm";
import BenchLoader from '../UIComponent/BenchLoader/BenchLoader';
import { BenchTable } from '../pages/UI';
import { ROUTES } from '../../routes/routesPaths';
import BenchSettingsBreadcrumbs from '../UIComponent/Components/BenchSettingsBreadcrumbs';
import { getEmployeeLoans, addRepayment } from '../Loan/LoanTypes';
import RecordPayrollPaymentModal from './RecordPayrollPaymentModal';
import BenchPaper from '../pages/UI/BenchPaper';
import BenchTopHeader from '../UIComponent/Components/BenchTopHeader';

// Styles
const useStyles = createStyles((theme) => ({
    payRunCard: {
        backgroundColor: '#fff9e6',
        border: '1px solid #ffe066',
        borderLeft: '4px solid #ffd43b',
    },
    summaryCard: {
        backgroundColor: 'white',
        border: '1px solid #e9ecef',
        height: '100%',
    },
    deductionCard: {
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        padding: theme.spacing.md,
        borderRadius: theme.radius.md,
        textAlign: 'center',
    },
    chartCard: {
        backgroundColor: 'white',
        borderRadius: theme.radius.md,
        padding: theme.spacing.lg,
        border: '1px solid #e9ecef',
    },
    employeeSummaryCard: {
        backgroundColor: 'white',
        border: '1px solid #e9ecef',
        textAlign: 'center',
        padding: theme.spacing.xl,
    },
}));

// Types
interface PayrollPeriod {
    id: string;
    company_id: string;
    created_at: any;
    end_date: string;
    message: string;
    month: string;
    pay_cycle_frequency: string;
    pay_period: string;
    payroll_process_date: string;
    process_date: any;
    start_date: string;
    total_employees: number;
    total_payout: number;
    year: number;
}

interface CalculatedSalary {
    basic: number;
    conveyance_allowance: number;
    helper_allowance: number;
    house_rent_allowance: number;
    medical_allowance: number;
    monthly_ctc: number;
}

export interface EmployeePayrollRecord {
    id: string;
    admin_id: string;
    branch_id: string;
    calculated_salary: CalculatedSalary;
    checkin_device_id: string;
    company_id: string;
    company_name: string;
    created_at: any;
    created_by: string;
    days_in_month: number;
    designation: string;
    email: string;
    first_name: string;
    fixed_monthly_breakdown: CalculatedSalary;
    fixed_monthly_ctc: number;
    holiday_count: number;
    last_modified_by: string;
    last_modified_date: any;
    last_name: string;
    leave_days: number;
    mobile_number: string;
    monthly_ctc: string;
    name: string;
    non_working_days: number;
    paid_leave_count: number;
    present_days: number;
    status: string;
    total_days: number;
    total_payable_days: number;
    type: string;
    uid: string;
    payment_status?: string;
    loan_deduction?: number;
    net_pay_after_deductions?: number;
}

export default function PayrollEmployeesPage() {
    const { classes } = useStyles();
    const { payrollId } = useParams<{ payrollId: string }>();
    const navigate = useNavigate();
    const companyId = useAppSelector((state: RootState) => state.session.company_id);

    const [payrollPeriod, setPayrollPeriod] = useState<PayrollPeriod | null>(null);
    const [employeeRecords, setEmployeeRecords] = useState<EmployeePayrollRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Adjustment modal state
    const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeePayrollRecord | null>(null);
    const [adjustmentAmount, setAdjustmentAmount] = useState<number>(0);
    const [adjustmentReason, setAdjustmentReason] = useState<string>('');

    // Side Panel State
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

    // Exit Process State
    const [exitModalOpen, setExitModalOpen] = useState(false);
    const [selectedExitEmployee, setSelectedExitEmployee] = useState<any>(null);

    // Record Payment Modal State
    const [recordPaymentModalOpen, setRecordPaymentModalOpen] = useState(false);


    const getfunctionlocation = getFunctions(app, 'asia-south1');

    const handleRecordPayment = async (selectedIds: string[], paymentDetails: any) => {
        if (!companyId || !payrollId) return;

        try {
            // Call Cloud Function
            const recordPaymentFn = httpsCallable(getfunctionlocation, 'recordPayment');
            const result = await recordPaymentFn({
                companyId,
                payrollId,
                selectedEmployeeIds: selectedIds,
                paymentDetails
            });

            console.log("Payment recorded result:", result.data);

            // Update local state
            setEmployeeRecords(prev => prev.map(emp =>
                selectedIds.includes(emp.id)
                    ? {
                        ...emp,
                        payment_status: 'Paid',
                        payment_details: paymentDetails
                    }
                    : emp
            ));

            toast.success(`Successfully recorded payment for ${selectedIds.length} employees`);
        } catch (error) {
            console.error('Error recording payments:', error);
            toast.error('Failed to record payments');
        }
    };

    const handleInitiateExit = (employee: any, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row click
        setSelectedExitEmployee(employee);
        setExitModalOpen(true);
    };

    const handleExitSubmit = async (values: any) => {
        console.log("Exit details submitted:", values);
        try {
            const payload = {
                ...values,
                employeeId: selectedExitEmployee?.id,
            }
            console.log("Payload", payload)
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            toast.success("Exit process initiated successfully");
            setExitModalOpen(false);
        } catch (error) {
            console.error("Error initiating exit:", error);
            toast.error("Failed to initiate exit process");
        }
    };

    const handleRowClick = (employeeId: string) => {
        setSelectedEmployeeId(prev => prev === employeeId ? null : employeeId);
    };

    const handleCloseSidePanel = () => {
        setSelectedEmployeeId(null);
    };

    const handleSaveSidePanel = async (updatedEmployee: any) => {
        if (!companyId || !payrollId || !updatedEmployee.id) return;

        try {
            setLoading(true);
            const employeeRef = doc(
                db,
                'companyprofile',
                companyId,
                'payroll',
                payrollId,
                'employeepayrollrecords',
                updatedEmployee.id
            );

            // Construct payload to update only necessary fields
            const updatePayload = {
                lop_days: updatedEmployee.lop_days || 0,
                adjustment_days: updatedEmployee.adjustment_days || 0,
                total_payable_days: updatedEmployee.total_payable_days,
                calculated_salary: updatedEmployee.calculated_salary,
                last_modified_date: new Date()
            };

            await updateDoc(employeeRef, updatePayload);

            // Update local state immediately
            const updatedRecords = employeeRecords.map(emp =>
                emp.id === updatedEmployee.id
                    ? { ...emp, ...updatePayload }
                    : emp
            );
            setEmployeeRecords(updatedRecords);

            // Recalculate total payout
            if (payrollPeriod) {
                const newTotalPayout = updatedRecords.reduce((sum, emp) => {
                    return sum + (emp.calculated_salary?.monthly_ctc || 0);
                }, 0);

                // Update payroll period document
                const payrollDocRef = doc(db, 'companyprofile', companyId, 'payroll', payrollId);
                await updateDoc(payrollDocRef, {
                    total_payout: newTotalPayout
                });

                // Update local payroll period state
                setPayrollPeriod({
                    ...payrollPeriod,
                    total_payout: newTotalPayout
                });
            }

            toast.success('Employee payroll updated successfully');
            handleCloseSidePanel();

        } catch (error) {
            console.error("Error saving employee payroll:", error);
            toast.error("Failed to save employee payroll changes");
        } finally {
            setLoading(false);
        }
    };

    const selectedEmployeeData = employeeRecords.find(e => e.id === selectedEmployeeId);

    // Breadcrumb links
    const breadcrumbLinks = [
        { label: 'Payroll', path: ROUTES.PAYROLL_MAIN_PAGE },
        { label: 'Pay Run Details' }
    ];

    useEffect(() => {
        const fetchPayrollDetails = async () => {
            if (!companyId || !payrollId) {
                setError('Missing company ID or payroll ID');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // Fetch payroll period details
                const payrollDocRef = doc(db, 'companyprofile', companyId, 'payroll', payrollId);
                const payrollDoc = await getDoc(payrollDocRef);

                if (!payrollDoc.exists()) {
                    setError('Payroll period not found');
                    setLoading(false);
                    return;
                }

                setPayrollPeriod({ id: payrollDoc.id, ...payrollDoc.data() } as PayrollPeriod);

                // Fetch employee records
                const employeeRecordsRef = collection(
                    db,
                    'companyprofile',
                    companyId,
                    'payroll',
                    payrollId,
                    'employeepayrollrecords'
                );

                const querySnapshot = await getDocs(employeeRecordsRef);
                const records: EmployeePayrollRecord[] = [];

                querySnapshot.forEach((doc) => {
                    records.push({ id: doc.id, ...doc.data() } as EmployeePayrollRecord);
                });

                setEmployeeRecords(records);
                console.log(records);
                setError(null);
            } catch (err) {
                console.error('Error fetching payroll details:', err);
                setError('Failed to load payroll details');
            } finally {
                setLoading(false);
            }
        };

        fetchPayrollDetails();
    }, [companyId, payrollId]);

    const formatCurrency = (amount: number) => {
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    // Calculate deductions
    const calculateDeductions = () => {
        const totalEPF = employeeRecords.reduce((sum, emp) => sum + (emp.calculated_salary?.basic * 0.12 || 0), 0);
        const totalESI = employeeRecords.reduce((sum, emp) => sum + (emp.calculated_salary?.monthly_ctc * 0.0075 || 0), 0);
        const totalTDS = employeeRecords.reduce((sum, emp) => sum + (emp.calculated_salary?.monthly_ctc * 0.05 || 0), 0);

        return { totalEPF, totalESI, totalTDS };
    };

    // Prepare chart data
    const getSalaryDistributionData = () => {
        const monthlyData = [];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        // Get current month index
        const currentMonthIndex = new Date().getMonth();

        // Generate data for each month up to current month
        for (let i = 0; i <= currentMonthIndex; i++) {
            const totalNetPay = employeeRecords.reduce((sum, emp) => sum + (emp.calculated_salary?.monthly_ctc || 0), 0);
            const totalTaxes = employeeRecords.reduce((sum, emp) => sum + (emp.calculated_salary?.monthly_ctc * 0.05 || 0), 0);
            const totalStatutories = employeeRecords.reduce((sum, emp) => sum + (emp.calculated_salary?.basic * 0.12 || 0), 0);
            const totalDeductions = employeeRecords.reduce((sum, emp) => sum + (emp.calculated_salary?.monthly_ctc * 0.0075 || 0), 0);

            monthlyData.push({
                month: months[i],
                'Net Pay': Math.round(totalNetPay),
                'Taxes': Math.round(totalTaxes),
                'Statutories': Math.round(totalStatutories),
                'Deductions': Math.round(totalDeductions),
            });
        }

        return monthlyData;
    };

    // Handle adjustment
    const handleOpenAdjustment = (employee: EmployeePayrollRecord) => {
        setSelectedEmployee(employee);
        setAdjustmentAmount(0);
        setAdjustmentReason('');
        setAdjustmentModalOpen(true);
    };

    const handleSaveAdjustment = () => {
        if (selectedEmployee) {
            console.log('Saving adjustment for:', selectedEmployee.name);
            console.log('Amount:', adjustmentAmount);
            console.log('Reason:', adjustmentReason);

            // Here you would typically save to Firestore
            // For now, just close the modal
            setAdjustmentModalOpen(false);
            setSelectedEmployee(null);
            setAdjustmentAmount(0);
            setAdjustmentReason('');
        }
    };

    if (error || !payrollPeriod) {
        return (
            <Container size="xl" py="xl">
                <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
                    {error || 'Payroll period not found'}
                </Alert>
            </Container>
        );
    }
    // Calculate totals for the new UI
    const totalGrossPay = employeeRecords.reduce((sum, emp) => sum + (emp.calculated_salary?.monthly_ctc || 0), 0);


    // const handleStatusChange = async (employeeId: string, newStatus: string) => {
    //     try {
    //         const employeeRef = doc(
    //             db,
    //             'companyprofile',
    //             companyId,
    //             'payroll',
    //             payrollId!,
    //             'employeepayrollrecords',
    //             employeeId
    //         );

    //         await updateDoc(employeeRef, {
    //             payment_status: newStatus
    //         });

    //         // Update local state
    //         setEmployeeRecords(prev => prev.map(emp =>
    //             emp.id === employeeId ? { ...emp, payment_status: newStatus } : emp
    //         ));

    //     } catch (error) {
    //         console.error('Error updating payment status:', error);
    //         // Optionally show error notification
    //     }
    // };

    // const handleSyncLoanDeductions = async () => {
    //     if (!companyId || !payrollId) return;

    //     try {
    //         setLoading(true);
    //         toast.info("Syncing loan deductions...");

    //         // 1. Fetch all loans for the company
    //         const allLoans = await getEmployeeLoans(companyId);

    //         // 2. Iterate over currently displayed payroll employees and prepare updates
    //         const updatedRecords = [...employeeRecords];
    //         const updates = [];

    //         for (let i = 0; i < updatedRecords.length; i++) {
    //             const emp = updatedRecords[i];
    //             // Find active loans for this employee where status is ACTIVE
    //             // Match by uid (assuming employee user ID) or id (payroll record ID might differ from user ID).
    //             // Typically existing systems use 'uid' for User ID. The loan record has 'employee_id'.
    //             // Let's assume emp.uid corresponds to loan.employee_id.
    //             // If emp.uid is missing, fall back to emp.id but check data structure.
    //             // EmployeePayrollRecord has 'uid'.
    //             const employeeIdToMatch = emp.uid;

    //             if (!employeeIdToMatch) {
    //                 console.warn(`Employee ${emp.name} has no UID, skipping loan check.`);
    //                 continue;
    //             }

    //             const activeLoans = allLoans.filter(loan =>
    //                 loan.employee_id === employeeIdToMatch &&
    //                 loan.status === 'ACTIVE'
    //             );

    //             // Default standard deductions
    //             const basic = emp.calculated_salary.basic || 0;
    //             const monthlyCtc = emp.calculated_salary.monthly_ctc || 0;
    //             const epf = basic * 0.12;
    //             const esi = monthlyCtc * 0.0075;
    //             const tds = monthlyCtc * 0.05;
    //             const standardDeductions = epf + esi + tds;

    //             if (activeLoans.length > 0) {
    //                 // Calculate total deduction for this month
    //                 const totalDeduction = activeLoans.reduce((sum, loan) => sum + loan.emi_amount, 0);
    //                 const netPay = monthlyCtc - standardDeductions - totalDeduction;

    //                 updatedRecords[i] = {
    //                     ...emp,
    //                     loan_deduction: totalDeduction,
    //                     net_pay_after_deductions: netPay
    //                 };

    //                 const employeeRef = doc(db, 'companyprofile', companyId, 'payroll', payrollId, 'employeepayrollrecords', emp.id);
    //                 updates.push(updateDoc(employeeRef, {
    //                     loan_deduction: totalDeduction,
    //                     net_pay_after_deductions: netPay
    //                 }));
    //             } else if (emp.loan_deduction && emp.loan_deduction > 0) {
    //                 // Reset if no active loans but record has deduction
    //                 const netPay = monthlyCtc - standardDeductions;

    //                 updatedRecords[i] = {
    //                     ...emp,
    //                     loan_deduction: 0,
    //                     net_pay_after_deductions: netPay
    //                 };

    //                 const employeeRef = doc(db, 'companyprofile', companyId, 'payroll', payrollId, 'employeepayrollrecords', emp.id);
    //                 updates.push(updateDoc(employeeRef, {
    //                     loan_deduction: 0,
    //                     net_pay_after_deductions: netPay
    //                 }));
    //             }
    //         }

    //         if (updates.length > 0) {
    //             await Promise.all(updates);
    //             setEmployeeRecords(updatedRecords);
    //             toast.success(`Synced deductions for ${updates.length} employees`);
    //         } else {
    //             toast.info("No active loans found to sync");
    //         }

    //     } catch (error) {
    //         console.error("Error syncing loans:", error);
    //         toast.error("Failed to sync loan deductions");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    return (
        <div className="page">

            <Container size="lg" py="md"> <Box
            sx={(theme) => ({ 
              position: "sticky",
              top: 110, // Avoid overlapping the fixed navbar
              zIndex: 100,
              backgroundColor: "#FAFAFA",
              paddingTop: theme.spacing.sm,
              //paddingBottom: theme.spacing.xs,
              marginTop: `-${theme.spacing.sm}`,
            })}
          >
                <SimpleGrid cols={2}>
                    <BenchSettingsBreadcrumbs links={breadcrumbLinks} />
                    <Group position="right">
                        {/* <Button
                                onClick={handleSyncLoanDeductions}
                                variant="light"
                                color="grape"
                            >
                                Sync Loan Deductions
                            </Button> */}
                        <Button
                            onClick={() => setRecordPaymentModalOpen(true)}
                        >
                            Record Payment
                        </Button>


                    </Group>
                </SimpleGrid>
                <Divider my="sm" /></Box>
                <Stack spacing="lg">
                    <BenchTopHeader
                        icon={<IconPercentage />}
                        title="Pay Run Details"
                        description="Manage and review employee payroll for this period"
                    />
                    {/* Breadcrumb */}



                    {/* Pay Run Summary */}
                    {/* Pay Run Summary - New Design (No Tax Card) */}
                    <Grid gutter="md">
                        {/* Payroll Period Card */}
                        <Grid.Col span={7}>
                            <Paper p="lg" radius="md" withBorder className={classes.summaryCard}>
                                <Group position="apart" align="flex-start">
                                    <Group spacing="xs">
                                        <ThemeIcon color="red" variant="light" size={36} radius="md">
                                            <IconCalendar size={16} stroke={1.5} />
                                        </ThemeIcon>

                                        <div>
                                            <Text size="xs" color="dimmed" weight={600} transform="uppercase">
                                                Payroll Period
                                            </Text>

                                            <Group spacing={4} mt={2}>
                                                <Title order={5} weight={700}>
                                                    {payrollPeriod.month} {payrollPeriod.year}
                                                </Title>
                                                <ActionIcon size="xs" variant="subtle" color="gray">
                                                    <IconPencil size={11} />
                                                </ActionIcon>
                                            </Group>

                                            <Text size="xs" color="dimmed" mt={2}>
                                                31 Base Days Included
                                            </Text>
                                        </div>
                                    </Group>

                                    <Badge color="green" variant="light" size="xs">
                                        On Track
                                    </Badge>
                                </Group>

                                <Divider my="sm" />

                                <Grid>
                                    <Grid.Col span={6}>
                                        <Text size={rem(20)} weight={700} lh={1.1}>
                                            {formatCurrency(totalGrossPay)}
                                        </Text>
                                        <Text size="xs" color="dimmed" mt={4}>
                                            Payroll Cost
                                        </Text>
                                    </Grid.Col>

                                    <Grid.Col span={6}>
                                        <Text size={rem(20)} weight={700} lh={1.1}>
                                            {formatCurrency(payrollPeriod.total_payout)}
                                        </Text>
                                        <Text size="xs" color="dimmed" mt={4}>
                                            Total Net Pay
                                        </Text>
                                    </Grid.Col>
                                </Grid>
                            </Paper>
                        </Grid.Col>

                        {/* Upcoming Pay Day Card */}
                        <Grid.Col span={5}>
                            <Paper p="lg" radius="md" withBorder className={classes.summaryCard}>
                                <Group position="apart" align="center">
                                    <div>
                                        <Group spacing="xs" mb={4}>
                                            <Badge color="green" variant="light" size="xs">
                                                Upcoming Pay Day
                                            </Badge>
                                            <Text size="xs" color="dimmed">
                                                Auto-Scheduled
                                            </Text>
                                        </Group>

                                        <Text size={rem(32)} weight={600} lh={1}>
                                            {new Date(
                                                payrollPeriod.process_date?.toDate
                                                    ? payrollPeriod.process_date.toDate()
                                                    : payrollPeriod.process_date
                                            )
                                                .getDate()
                                                .toString()
                                                .padStart(2, "0")}
                                        </Text>

                                        <Text size="sm" weight={500} color="dimmed" mt={2}>
                                            {new Date(
                                                payrollPeriod.process_date?.toDate
                                                    ? payrollPeriod.process_date.toDate()
                                                    : payrollPeriod.process_date
                                            ).toLocaleString("default", {
                                                month: "short",
                                                year: "numeric",
                                            })}
                                        </Text>
                                    </div>

                                    <Paper p="sm" radius="md" withBorder style={{ minWidth: 140 }}>
                                        <Group spacing="xs">
                                            <ThemeIcon color="red" variant="light" size={36} radius="xl">
                                                <IconUsers size={18} stroke={1.5} />
                                            </ThemeIcon>

                                            <div>
                                                <Text size={rem(18)} weight={700} lh={1}>
                                                    {payrollPeriod.total_employees}
                                                </Text>
                                                <Text size="xs" color="dimmed" mt={2}>
                                                    Active Employees
                                                </Text>
                                            </div>
                                        </Group>
                                    </Paper>
                                </Group>
                            </Paper>
                        </Grid.Col>
                    </Grid>





                    {/* Main Content Area with Side Panel Support */}
                    <Grid gutter="md">
                        <Grid.Col span={selectedEmployeeId ? 8 : 12} style={{ transition: 'all 0.3s ease' }}>
                            {/* Employee Records Table */}
                            <BenchPaper title={'Employee Payroll Records'}>
                                {/* <Title order={4} mb="md">Employee Payroll Records</Title> */}

                                {employeeRecords.length === 0 ? (
                                    <Text align="center" color="dimmed" py="xl">
                                        No employee records found for this payroll period
                                    </Text>
                                ) : (
                                    <ScrollArea style={{ height: 'calc(100vh - 400px)' }}>
                                        <BenchTable>
                                            <thead>
                                                <tr>
                                                    <th>Employee Name</th>
                                                    <th>Email</th>
                                                    <th>Designation</th>
                                                    <th>Payable Days</th>
                                                    <th>Fixed CTC</th>
                                                    <th>Calculated Salary</th>
                                                    <th>Loan Deduction</th>
                                                    <th>Payment Status</th>
                                                    {/* <th>Action</th> */}

                                                </tr>
                                            </thead>
                                            <tbody>
                                                {employeeRecords.map((employee) => (
                                                    <tr
                                                        key={employee.id}
                                                        onClick={() => handleRowClick(employee.id)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: selectedEmployeeId === employee.id ? '#F0F3FF' : undefined
                                                        }}
                                                    >
                                                        <td>
                                                            <Text weight={500}>
                                                                {employee.name || `${employee.first_name} ${employee.last_name}`.trim()}
                                                            </Text>
                                                        </td>
                                                        <td>
                                                            <Text size="sm">{employee.email || 'N/A'}</Text>
                                                        </td>
                                                        <td>
                                                            <Text size="sm">{employee.designation || 'N/A'}</Text>
                                                        </td>
                                                        <td>
                                                            <Text>{employee.total_payable_days}</Text>
                                                        </td>
                                                        <td>
                                                            <Text>{formatCurrency(employee.fixed_monthly_ctc || 0)}</Text>
                                                        </td>
                                                        <td>
                                                            <Text weight={500} color="#4c6ef5">
                                                                {formatCurrency(employee.calculated_salary?.monthly_ctc || 0)}
                                                            </Text>
                                                        </td>
                                                        <td>
                                                            <Text weight={500} color="#4c6ef5">
                                                                Nil
                                                            </Text>
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                color={'green'}
                                                                variant="light"
                                                            >
                                                                {employee.payment_status || 'Paid'}
                                                            </Badge>
                                                        </td>
                                                        {/* <td>
                                                            <Menu shadow="md" width={200} withinPortal>
                                                                <Menu.Target>
                                                                    <ActionIcon onClick={(e) => e.stopPropagation()}>
                                                                        <IconDotsVertical size={16} />
                                                                    </ActionIcon>
                                                                </Menu.Target>

                                                                <Menu.Dropdown>
                                                                    <Menu.Item
                                                                        icon={<IconDoorExit size={14} />}
                                                                        color="red"
                                                                        onClick={(e) => handleInitiateExit(employee, e)}
                                                                    >
                                                                        Start Exit Process
                                                                    </Menu.Item>
                                                                </Menu.Dropdown>
                                                            </Menu>
                                                        </td> */}

                                                    </tr>
                                                ))}
                                            </tbody>
                                        </BenchTable>
                                    </ScrollArea>

                                )}
                            </BenchPaper>
                        </Grid.Col>

                        {/* Side Panel Column */}
                        {selectedEmployeeId && (
                            <Grid.Col span={4}>
                                <Paper shadow="md" style={{ height: '100%', border: '1px solid #e9ecef', overflow: 'hidden' }}>
                                    <PayrollEmployeeSidePanel
                                        employee={selectedEmployeeData}
                                        onClose={handleCloseSidePanel}
                                        onSave={handleSaveSidePanel}
                                    />
                                </Paper>
                            </Grid.Col>
                        )}
                    </Grid>

                    <ReusableModal
                        opened={exitModalOpen}
                        onClose={() => setExitModalOpen(false)}
                        title="Employee Exit Details"
                        size="xl"
                    >
                        <ExitDetailsForm
                            onClose={() => setExitModalOpen(false)}
                            onSubmit={handleExitSubmit}
                            employeeData={selectedExitEmployee}
                        />
                    </ReusableModal>

                    <RecordPayrollPaymentModal
                        opened={recordPaymentModalOpen}
                        onClose={() => setRecordPaymentModalOpen(false)}
                        employees={employeeRecords}
                        onConfirm={handleRecordPayment}
                    />
                </Stack>
            </Container>


            <BenchLoader isLoading={loading} />
        </div>
    );
}
